// Routes through Fly proxy to sidestep osu! CORS and shared-egress rate limits.
const PROXY_BASE = 'https://proxy.replayviewer.com/osu-proxy';
const API_BASE = `${PROXY_BASE}/api/v2`;

export type ScoreRuleset = 'osu' | 'taiko' | 'fruits' | 'mania';

// osu! reachable but degraded — Cloudflare block page, rate-limit, or 5xx. Distinct from
// a genuine auth failure: the token is fine, osu! itself is unavailable (e.g. the ongoing
// DDoS, during which Cloudflare serves a 403 HTML block for server-side /api/v2 traffic).
export const OSU_ISSUES_MESSAGE = 'osu! appears to be having issues — please try again later! manual upload still works';

export class OsuUnavailableError extends Error {
  constructor(message: string = OSU_ISSUES_MESSAGE) {
    super(message);
    this.name = 'OsuUnavailableError';
  }
}

// Real session death only (revoked/expired/scope-downgraded token); transient and
// osu!-side failures must NOT be this class — callers logout() on AuthError.
export class AuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthError';
  }
}

// A Cloudflare block returns HTML, not osu!'s JSON error body. Treat a 403-without-JSON,
// a 429, or any 5xx as "osu! unavailable"; a genuine osu! 401/403/404 carries a JSON body
// and falls through to the caller's normal error (so real auth/not-found stay intact).
export function isUpstreamUnavailable(resp: Response): boolean {
  if (resp.status === 429 || resp.status >= 500) return true;
  if (resp.status !== 403) return false;
  return !(resp.headers.get('content-type') || '').includes('application/json');
}

// Shared classification for non-ok responses from authenticated endpoints:
// JSON 401/403 = genuine auth failure → AuthError (callers downgrade to logged-out);
// Cloudflare block / 429 / 5xx → OsuUnavailableError; anything else returns so the
// caller throws its own contextual Error (e.g. 404 score not found).
export function throwIfAuthOrUnavailable(resp: Response): void {
  if (resp.status === 401 || resp.status === 403) {
    if ((resp.headers.get('content-type') || '').includes('application/json')) {
      throw new AuthError(`unauthorized (HTTP ${resp.status})`);
    }
    throw new OsuUnavailableError();
  }
  if (isUpstreamUnavailable(resp)) throw new OsuUnavailableError();
}

export interface ScoreRef {
  id: string;
  // null for lazer namespace; legacy stable scores 404 without the mode prefix.
  ruleset: ScoreRuleset | null;
}

export function parseScoreRef(input: string): ScoreRef | null {
  // Zero-width/BOM/bidi marks survive partial edits of pasted URLs and break /^\d+$/.
  const s = input.replace(/[\s\u200B-\u200F\uFEFF]/g, '');
  if (s === '') return null;
  if (/^\d+$/.test(s)) return { id: s, ruleset: null };
  // Bare `mode/{id}` round-trips ruleset through share links.
  const bare = /^(osu|taiko|fruits|mania)\/(\d+)$/i.exec(s);
  if (bare !== null) return { id: bare[2]!, ruleset: bare[1]!.toLowerCase() as ScoreRuleset };
  const m = /\/scores\/(?:(osu|taiko|fruits|mania)\/)?(\d+)/i.exec(s);
  if (m === null) return null;
  return { id: m[2]!, ruleset: m[1] !== undefined ? m[1].toLowerCase() as ScoreRuleset : null };
}

export interface BeatmapRef {
  beatmapId: number;
  // null when only a bare `beatmaps/{id}` link was given (no set fragment) — the
  // caller resolves it (e.g. via the .osz download, which derives the set).
  setId: number | null;
  // Fragment ruleset (`osu|taiko|fruits|mania`). Informational only — the beatmap's
  // own [General] Mode is authoritative for which generator runs.
  ruleset: ScoreRuleset | null;
}

// Parses the canonical share link `https://osu.ppy.sh/beatmapsets/4651#osu/24722`
// (and the bare `https://osu.ppy.sh/beatmaps/24722` single-difficulty form) into ids.
// Mirrors parseScoreRef's zero-width/BOM stripping for pasted URLs.
export function parseBeatmapRef(input: string): BeatmapRef | null {
  const s = input.replace(/[\s\u200B-\u200F\uFEFF]/g, '');
  if (s === '') return null;

  // Bare numeric beatmapId (what our own #auto= share link emits; also a convenient paste form).
  if (/^\d+$/.test(s)) return { beatmapId: Number(s), setId: null, ruleset: null };

  // Full beatmapset URL with `#<ruleset>/<beatmapId>` fragment (the share link).
  const set = /beatmapsets\/(\d+)#(osu|taiko|fruits|mania)\/(\d+)/i.exec(s);
  if (set !== null) {
    return {
      beatmapId: Number(set[3]),
      setId: Number(set[1]),
      ruleset: set[2]!.toLowerCase() as ScoreRuleset,
    };
  }

  // Bare single-difficulty link `beatmaps/{id}` — setId resolved later.
  const bare = /beatmaps\/(\d+)/i.exec(s);
  if (bare !== null) return { beatmapId: Number(bare[1]), setId: null, ruleset: null };

  return null;
}

export async function downloadReplay(
  scoreId: string,
  token: string,
  opts: { ruleset?: ScoreRuleset | null } = {},
): Promise<ArrayBuffer> {
  const path = opts.ruleset !== undefined && opts.ruleset !== null
    ? `${opts.ruleset}/${scoreId}`
    : scoreId;
  const resp = await fetch(`${API_BASE}/scores/${path}/download`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!resp.ok) {
    throwIfAuthOrUnavailable(resp);
    throw new Error(`replay download failed for score ${scoreId} (HTTP ${resp.status})`);
  }
  return resp.arrayBuffer();
}

// Canonical single-difficulty .osu text straight from osu! (not a mirror's .osz).
// Public endpoint — token optional. Bytes hash to the beatmap's live checksum,
// so this is the authoritative source when a mirror's .osz is stale.
export async function downloadBeatmapOsu(beatmapId: number, token?: string | null): Promise<ArrayBuffer> {
  const headers: Record<string, string> = { 'Accept': 'text/plain' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const resp = await fetch(`${PROXY_BASE}/osu/${beatmapId}`, { headers });
  if (!resp.ok) {
    throwIfAuthOrUnavailable(resp);
    throw new Error(`.osu download failed for beatmap ${beatmapId} (HTTP ${resp.status})`);
  }
  return resp.arrayBuffer();
}

interface BeatmapLookup {
  beatmapId:    number;
  beatmapsetId: number;
  title:        string;
  version:      string;
}

export interface OsuUserProfile {
  userId:      number;
  username:    string;
  avatarUrl:   string | null;
  countryCode: string | null;
  countryName: string | null;
  // null if inactive or never played standard.
  globalRank:  number | null;
  countryRank: number | null;
  pp:          number | null;
  // Lazer quick play (2-player): wins=first_placements, losses=plays−first_placements.
  matchmaking: {
    poolName:      string | null;
    rank:          number | null;
    rating:        number | null;
    plays:         number | null;
    wins:          number | null;
    losses:        number | null;
    isProvisional: boolean;
  } | null;
  // Kept for future field lifting without re-fetching.
  raw:         Record<string, unknown>;
}

// Throws on non-2xx; callers catch and fall back to a minimal card.
export async function fetchUserProfile(userId: number, token: string): Promise<OsuUserProfile> {
  const url = `${API_BASE}/users/${userId}/osu`;
  const resp = await fetch(url, {
    headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
  });
  if (!resp.ok) {
    throwIfAuthOrUnavailable(resp);
    throw new Error(`user fetch failed for ${userId} (HTTP ${resp.status})`);
  }
  const data = await resp.json() as Record<string, unknown>;

  const stats = (data['statistics'] ?? {}) as Record<string, unknown>;
  const country = (data['country'] ?? {}) as Record<string, unknown>;

  const globalRankRaw  = stats['global_rank'];
  const countryRankRaw = stats['country_rank'];
  const ppRaw = stats['pp'];

  const matchmaking = pickMatchmakingStats(data['matchmaking_stats']);

  return {
    userId,
    username:    typeof data['username']     === 'string' ? data['username']     as string : `user ${userId}`,
    avatarUrl:   typeof data['avatar_url']   === 'string' ? data['avatar_url']   as string : null,
    countryCode: typeof data['country_code'] === 'string' ? data['country_code'] as string : null,
    countryName: typeof country['name']      === 'string' ? country['name']      as string : null,
    globalRank:  typeof globalRankRaw  === 'number' ? globalRankRaw  : null,
    countryRank: typeof countryRankRaw === 'number' ? countryRankRaw : null,
    pp:          typeof ppRaw          === 'number' ? ppRaw          : null,
    matchmaking,
    raw:         data,
  };
}

function numOrNull(v: unknown): number | null {
  return typeof v === 'number' ? v : null;
}

function pickMatchmakingStats(raw: unknown): OsuUserProfile['matchmaking'] {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const entries = raw.filter((e): e is Record<string, unknown> => e !== null && typeof e === 'object');
  if (entries.length === 0) return null;

  // Prefer the active (seasonal) pool; fall back to first entry.
  const activeEntry = entries.find((e) => {
    const pool = e['pool'];
    return pool !== null && typeof pool === 'object'
      && (pool as Record<string, unknown>)['active'] === true;
  });
  const entry = activeEntry ?? entries[0]!;
  const pool = (entry['pool'] ?? {}) as Record<string, unknown>;

  const plays = numOrNull(entry['plays']);
  const wins  = numOrNull(entry['first_placements']);
  const losses = plays !== null && wins !== null ? Math.max(0, plays - wins) : null;

  return {
    poolName:      typeof pool['name'] === 'string' ? pool['name'] as string : null,
    rank:          numOrNull(entry['rank']),
    rating:        numOrNull(entry['rating']),
    plays,
    wins,
    losses,
    isProvisional: entry['is_rating_provisional'] === true,
  };
}

export async function lookupBeatmapByHash(checksum: string, token: string): Promise<BeatmapLookup> {
  const url = `${API_BASE}/beatmaps/lookup?checksum=${encodeURIComponent(checksum)}`;
  const resp = await fetch(url, {
    headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
  });
  if (!resp.ok) {
    throwIfAuthOrUnavailable(resp);
    throw new Error(`beatmap lookup failed for hash ${checksum} (HTTP ${resp.status})`);
  }
  const data = await resp.json() as {
    id: number; beatmapset_id: number; version: string;
    beatmapset?: { title?: string };
  };
  // Reject null/0/missing setId — would poison the persisted hashToSetId cache.
  if (!Number.isInteger(data.beatmapset_id) || data.beatmapset_id <= 0) {
    throw new Error(
      `beatmap lookup for hash ${checksum} returned invalid beatmapset_id: ${String(data.beatmapset_id)}`,
    );
  }
  return {
    beatmapId:    data.id,
    beatmapsetId: data.beatmapset_id,
    title:        data.beatmapset?.title ?? '',
    version:      data.version,
  };
}
